import { useMemo, useRef, useState } from 'react'
import { ImagePlus, Loader2, Paperclip, Search, X } from 'lucide-react'

import { AttachmentCard } from '@/components/attachment-card'
import { Avatar } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { Input } from '@/components/ui/field'
import { useToast } from '@/components/ui/toast'
import { useSession } from '@/features/auth/api'
import {
  useCreatePost,
  useMyListings,
  useUploadPostImage,
  type PostAttachment,
} from '@/features/sosial/api'
import { ApiError } from '@/lib/api'
import { cn } from '@/lib/cn'

const maxGambar = 4
const maxHuruf = 3000

/**
 * PostComposer adalah kotak tulis lini masa.
 *
 * Gambar diunggah begitu dipilih, bukan saat tombol Kirim ditekan: dengan
 * begitu penulis melihat hasilnya sebelum memutuskan, dan kiriman yang
 * gambarnya gagal diunggah tidak pernah terkirim setengah jadi.
 */
export function PostComposer({
  groupId,
  placeholder,
  autoFocus,
  onTerkirim,
  className,
}: {
  className?: string
  groupId?: string
  placeholder?: string
  autoFocus?: boolean
  /** Dipanggil setelah kiriman tersimpan, misalnya untuk menutup dialog. */
  onTerkirim?: () => void
}) {
  const { data: session } = useSession()
  const [isi, setIsi] = useState('')
  const [gambar, setGambar] = useState<string[]>([])
  const [lampiran, setLampiran] = useState<PostAttachment | null>(null)
  const [pemilihTerbuka, setPemilihTerbuka] = useState(false)
  const berkas = useRef<HTMLInputElement>(null)

  const kirim = useCreatePost()
  const unggah = useUploadPostImage()
  const toast = useToast()

  const sisaHuruf = maxHuruf - isi.length
  const adaIsi = isi.trim().length > 0 || gambar.length > 0 || lampiran !== null
  const bolehKirim = adaIsi && sisaHuruf >= 0 && !kirim.isPending

  const pilihGambar = (files: FileList | null) => {
    if (!files) return

    const muat = Math.min(files.length, maxGambar - gambar.length)
    if (muat <= 0) {
      toast.info('Maksimal empat gambar', 'Hapus salah satunya dulu untuk menambah yang lain.')
      return
    }

    for (let i = 0; i < muat; i++) {
      unggah.mutate(files[i]!, {
        onSuccess: (hasil) => setGambar((kini) => [...kini, hasil.image_url].slice(0, maxGambar)),
        onError: (error) =>
          toast.error('Gambar gagal diunggah', error instanceof Error ? error.message : undefined),
      })
    }
  }

  const bersihkan = () => {
    setIsi('')
    setGambar([])
    setLampiran(null)
  }

  return (
    <div className={cn('border-b border-line pb-4', className)}>
      <div className="flex gap-3">
        <Avatar name={session?.user.full_name ?? '?'} src={session?.user.avatar_url} size="utas" />

        <div className="min-w-0 flex-1 space-y-3">
          <textarea
            value={isi}
            onChange={(e) => setIsi(e.target.value)}
            rows={isi || autoFocus ? 4 : 1}
            autoFocus={autoFocus}
            placeholder={placeholder ?? 'Apa yang sedang Anda kerjakan?'}
            aria-label="Tulis kiriman"
            className="w-full resize-none bg-transparent py-1.5 text-[15px] text-ink outline-none placeholder:text-subtle"
          />

          {lampiran ? (
            <AttachmentCard lampiran={lampiran} onRemove={() => setLampiran(null)} />
          ) : null}

          {gambar.length > 0 ? (
            <div className="grid grid-cols-4 gap-2">
              {gambar.map((url) => (
                <div key={url} className="relative overflow-hidden rounded-lg border border-line">
                  <img src={url} alt="" className="aspect-square w-full object-cover" />
                  <button
                    type="button"
                    onClick={() => setGambar((kini) => kini.filter((g) => g !== url))}
                    aria-label="Hapus gambar"
                    className="absolute right-1 top-1 grid size-6 place-items-center rounded-full bg-black/60 text-white transition hover:bg-black/80"
                  >
                    <X className="size-3.5" />
                  </button>
                </div>
              ))}
            </div>
          ) : null}

          <div className="flex items-center justify-between gap-3">
            <div className="-ml-2 flex items-center gap-0.5">
              <input
                ref={berkas}
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                className="hidden"
                onChange={(e) => {
                  pilihGambar(e.target.files)
                  e.target.value = ''
                }}
              />

              <TombolAlat
                label="Tambah gambar"
                onClick={() => berkas.current?.click()}
                disabled={unggah.isPending || gambar.length >= maxGambar}
              >
                {unggah.isPending ? (
                  <Loader2 className="size-5 animate-spin" />
                ) : (
                  <ImagePlus className="size-5" />
                )}
              </TombolAlat>

              <TombolAlat label="Lampirkan produk atau jasa" onClick={() => setPemilihTerbuka(true)}>
                <Paperclip className="size-5" />
              </TombolAlat>

              {/* Sisa huruf baru ditampilkan ketika mulai mendekati batas.
                  Penghitung yang selalu terlihat mengubah kotak tulis menjadi
                  ujian, bukan tempat bercerita. */}
              {sisaHuruf < 300 ? (
                <span
                  className={`ml-1 text-[13px] ${sisaHuruf < 0 ? 'text-danger' : 'text-subtle'}`}
                >
                  {sisaHuruf}
                </span>
              ) : null}
            </div>

            <Button
              size="sm"
              disabled={!bolehKirim}
              onClick={() =>
                kirim.mutate(
                  {
                    body: isi.trim(),
                    images: gambar,
                    group_id: groupId,
                    attachment_kind: lampiran?.kind,
                    attachment_id: lampiran?.id,
                  },
                  {
                    onSuccess: () => {
                      bersihkan()
                      if (onTerkirim) {
                        // Dari dialog, lini masanya mungkin tidak sedang
                        // terlihat; tanpa kabar ini kiriman seolah lenyap.
                        toast.success('Kiriman terkirim')
                        onTerkirim()
                      }
                    },
                    onError: (error) =>
                      toast.error('Gagal mengirim', error instanceof ApiError ? error.message : undefined),
                  },
                )
              }
            >
              {kirim.isPending ? 'Mengirim…' : 'Kirim'}
            </Button>
          </div>
        </div>
      </div>

      <PemilihLampiran
        open={pemilihTerbuka}
        onClose={() => setPemilihTerbuka(false)}
        onPilih={(l) => {
          setLampiran(l)
          setPemilihTerbuka(false)
        }}
      />
    </div>
  )
}

function TombolAlat({
  children,
  label,
  onClick,
  disabled,
}: {
  children: React.ReactNode
  label: string
  onClick: () => void
  disabled?: boolean
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className="grid size-9 place-items-center rounded-full text-subtle transition hover:bg-sunken hover:text-ink disabled:pointer-events-none disabled:opacity-40"
    >
      {children}
    </button>
  )
}

/**
 * PemilihLampiran menawarkan produk dan jasa milik penulis sendiri.
 *
 * Hanya miliknya yang ditawarkan: yang dicari orang saat menulis adalah
 * mempromosikan dagangannya, bukan menelusuri katalog seluruh situs.
 */
function PemilihLampiran({
  open,
  onClose,
  onPilih,
}: {
  open: boolean
  onClose: () => void
  onPilih: (lampiran: PostAttachment) => void
}) {
  const { data, isLoading } = useMyListings()
  const [kueri, setKueri] = useState('')

  const daftar = useMemo(() => {
    const semua = data?.listings ?? []
    const q = kueri.trim().toLowerCase()
    if (!q) return semua
    return semua.filter((l) => l.title.toLowerCase().includes(q) || l.kind_label.toLowerCase().includes(q))
  }, [data, kueri])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Lampirkan produk atau jasa"
      description="Pembaca langsung melihat apa yang Anda tawarkan, beserta harganya."
      footer={
        <Button variant="outline" onClick={onClose}>
          Tutup
        </Button>
      }
    >
      <div className="space-y-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-subtle" />
          <Input
            value={kueri}
            onChange={(e) => setKueri(e.target.value)}
            placeholder="Cari di antara milik Anda"
            className="pl-10"
            aria-label="Cari produk atau jasa"
          />
        </div>

        {isLoading ? (
          <p className="py-6 text-center text-sm text-muted">Memuat…</p>
        ) : daftar.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted">
            {kueri
              ? 'Tidak ada yang cocok dengan pencarian itu.'
              : 'Anda belum punya kelas, paket jasa, lowongan, atau sayembara yang tayang. Yang masih berupa draf belum dapat dilampirkan.'}
          </p>
        ) : (
          <div className="max-h-[22rem] space-y-2 overflow-y-auto">
            {daftar.map((l) => (
              <button key={`${l.kind}-${l.id}`} type="button" onClick={() => onPilih(l)} className="block w-full text-left">
                <AttachmentCard lampiran={l} sebagai="polos" />
              </button>
            ))}
          </div>
        )}
      </div>
    </Dialog>
  )
}
